import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import { Flag, Maximize } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { getDb } from "@/db";
import { cars, events } from "@/db/schema";

const getSlideshowData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		return { event, cars: eventCars };
	});

export const Route = createFileRoute("/events/$eventId/slideshow")({
	loader: ({ params }) => getSlideshowData({ data: params.eventId }),
	component: SlideshowDisplay,
});

function SlideshowDisplay() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();

	const { data } = useQuery({
		queryKey: ["slideshow", params.eventId],
		queryFn: () => getSlideshowData({ data: params.eventId }),
		initialData,
		refetchInterval: 30000,
	});

	const { event, cars: eventCars } = data;

	// Only show cars that have an avatar or photo
	const displayCars = eventCars.filter((c) => c.avatarUrl || c.photoUrl);

	const [currentIndex, setCurrentIndex] = useState(0);
	const [visible, setVisible] = useState(true);

	// Parse interval from query params (default 6 seconds)
	const searchParams = new URLSearchParams(
		typeof window !== "undefined" ? window.location.search : "",
	);
	const interval = Number(searchParams.get("interval")) || 6;

	// Auto-advance with crossfade
	useEffect(() => {
		if (displayCars.length <= 1) return;

		const timer = setInterval(() => {
			setVisible(false);
			setTimeout(() => {
				setCurrentIndex((prev) => (prev + 1) % displayCars.length);
				setVisible(true);
			}, 500);
		}, interval * 1000);

		return () => clearInterval(timer);
	}, [displayCars.length, interval]);

	// Keep index in bounds when data changes
	useEffect(() => {
		if (currentIndex >= displayCars.length && displayCars.length > 0) {
			setCurrentIndex(0);
		}
	}, [displayCars.length, currentIndex]);

	const handleFullscreen = useCallback(() => {
		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else {
			document.documentElement.requestFullscreen();
		}
	}, []);

	const currentCar = displayCars[currentIndex];

	return (
		<div className="fixed inset-0 z-50 bg-background overflow-hidden flex flex-col">
			<style>{`
				@keyframes kenBurns {
					0% { transform: scale(1) translate(0, 0); }
					50% { transform: scale(1.08) translate(-1%, -1%); }
					100% { transform: scale(1) translate(0, 0); }
				}
			`}</style>

			{/* Header */}
			<div className="flex items-center justify-between px-6 pt-5 pb-3">
				<div className="flex items-center gap-4">
					{event.imageUrl ? (
						<img
							src={event.imageUrl}
							alt={event.name}
							className="h-14 w-auto rounded-lg object-cover"
						/>
					) : (
						<Flag size={32} className="text-cherry" />
					)}
					<div>
						<h1 className="text-3xl text-foreground font-bold">{event.name}</h1>
						<p className="text-muted-foreground text-sm">
							{event.date}
							{event.location && ` \u2022 ${event.location}`}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-3">
					<span className="text-muted-foreground text-sm">
						{currentIndex + 1} / {displayCars.length}
					</span>
					<button
						type="button"
						onClick={handleFullscreen}
						className="p-2 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
						title="Toggle fullscreen"
					>
						<Maximize size={18} />
					</button>
				</div>
			</div>

			{/* Slideshow content */}
			<div className="flex-1 flex items-center justify-center p-6">
				{displayCars.length === 0 ? (
					<div className="text-center">
						<p className="text-foreground text-3xl font-bold">No photos yet</p>
						<p className="text-muted-foreground text-lg mt-2">
							Upload car photos or generate avatars to see them here
						</p>
					</div>
				) : currentCar ? (
					<div
						className="max-w-4xl w-full flex flex-col items-center transition-opacity duration-500"
						style={{ opacity: visible ? 1 : 0 }}
					>
						{/* Image */}
						<div className="relative mb-6">
							{currentCar.avatarUrl && currentCar.photoUrl ? (
								<div className="flex items-end gap-4">
									<img
										src={currentCar.avatarUrl}
										alt={`${currentCar.carName ?? "Car"} avatar`}
										className="w-72 h-72 sm:w-96 sm:h-96 rounded-2xl object-cover shadow-2xl"
										style={{
											animation: `kenBurns ${interval}s ease-in-out infinite`,
										}}
									/>
									<img
										src={currentCar.photoUrl}
										alt={`${currentCar.carName ?? "Car"} photo`}
										className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl object-cover shadow-lg ring-4 ring-border"
									/>
								</div>
							) : (
								<img
									src={(currentCar.avatarUrl || currentCar.photoUrl) as string}
									alt={currentCar.carName ?? "Car"}
									className="w-72 h-72 sm:w-96 sm:h-96 rounded-2xl object-cover shadow-2xl"
									style={{
										animation: `kenBurns ${interval}s ease-in-out infinite`,
									}}
								/>
							)}
						</div>

						{/* Info */}
						<p className="text-foreground font-black text-4xl sm:text-5xl text-center">
							<span className="inline-flex items-center text-lg font-bold text-primary bg-primary/10 rounded px-2 py-1 mr-2 align-middle">
								#{currentCar.carNumber}
							</span>
							{currentCar.carName}
						</p>
						<p className="text-muted-foreground text-xl sm:text-2xl mt-2 text-center">
							{currentCar.driverName}
						</p>
						{currentCar.division && (
							<span className="mt-3 px-4 py-1.5 bg-primary/10 text-primary text-base font-bold rounded-full">
								{currentCar.division}
							</span>
						)}

						{/* Progress dots */}
						{displayCars.length > 1 && displayCars.length <= 30 && (
							<div className="flex gap-1.5 mt-6">
								{displayCars.map((_, i) => (
									<div
										key={`dot-${displayCars[i].id}`}
										className={`w-2 h-2 rounded-full transition-all ${
											i === currentIndex ? "bg-primary w-6" : "bg-muted"
										}`}
									/>
								))}
							</div>
						)}
					</div>
				) : null}
			</div>
		</div>
	);
}
